// static/table_engine_src/core/commands.js

/**
 * Commands/ops dispatcher.
 * Dispatches keyboard-level actions to the engine ops currently installed on ctx.
 *
 * OPTION A (single source of truth for UX):
 * - cmdCopy() is the ONLY place that shows "Copied ..." notifications.
 * - Therefore, copy implementations (copyToClipboard / copyRowsToTSV etc.)
 *   MUST NOT notify directly (or must respect ctx._teSuppressCopyNotify).
 *
 * IMPORTANT:
 * - DO NOT call onEdit/updateTotals/autosize/render here, because the underlying
 *   ops in the engine already do that today.
 */
export function installCommands(ctx) {
  if (!ctx || typeof ctx !== "object") return;

  function _notifyOk(msg) {
    try { ctx.notify?.ok?.(msg); } catch {}
  }

  function _copyMessage() {
    const s = ctx._sel || {};

    // Columns
    const nCols = s.selectedCols?.size || 0;
    if (nCols > 0) return `Copied ${nCols} column(s).`;

    // Rows
    const nRows = s.selectedRows?.size || 0;
    if (nRows > 0) return `Copied ${nRows} row(s).`;

    // Cell range / anchor
    const ranges = typeof ctx.getSelections === "function" ? ctx.getSelections() : [];
    if (ranges.length > 1) return `Copied ${ranges.length} ranges.`;

    const sel = ctx.getSelection?.();
    if (!sel) return "Copied.";

    const rows = (sel.r1 - sel.r0 + 1);
    const cols = (sel.c1 - sel.c0 + 1);

    if (rows === 1 && cols === 1) return "Copied cell.";
    return `Copied ${rows}×${cols} cells.`;
  }

  async function cmdClearSelection() {
    const s = ctx._sel || {};

    if (s.selectedCols && s.selectedCols.size) {
      ctx.clearSelectedColumns?.();
      return;
    }

    if (s.selectedRows && s.selectedRows.size) {
      ctx.clearSelectedRowsInputs?.();
      return;
    }

    const sel = ctx.getSelection?.();
    if (sel) {
      const ranges = typeof ctx.getSelections === "function" ? ctx.getSelections() : [sel];
      const uniq = new Set();
      for (const rg of ranges) {
        if (!rg) continue;
        const key = `${rg.r0}:${rg.r1}:${rg.c0}:${rg.c1}`;
        if (uniq.has(key)) continue;
        uniq.add(key);
        ctx.clearRange?.(rg);
      }
    }
  }

  async function cmdCopy() {
    let copied = false;

    // Ensure copy ops don't toast on their own (Option A).
    // copy implementations must respect this flag.
    ctx._teSuppressCopyNotify = true;

    try {
      // Prefer installed clipboard op
      if (typeof ctx.copyToClipboard === "function") {
        copied = !!(await ctx.copyToClipboard());
      }
    } finally {
      ctx._teSuppressCopyNotify = false;
    }

    if (copied) _notifyOk(_copyMessage());
  }

  async function cmdPaste() {
    await ctx.pasteFromClipboard?.();
  }

  async function cmdFillDown() {
    await ctx.fillDownFromSelection?.();
  }

  ctx.cmdClearSelection = cmdClearSelection;
  ctx.cmdCopy = cmdCopy;
  ctx.cmdPaste = cmdPaste;
  ctx.cmdFillDown = cmdFillDown;
}
